import React, { useEffect } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import {
  FileUpload,
  type FileUploadUploadEvent,
  type FileUploadSelectEvent,
  type ItemTemplateOptions,
  type FileUploadHeaderTemplateOptions,
  type FileUploadHandlerEvent,
} from 'primereact/fileupload';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import logoScreenshot from '../../../../assets/img_logo_scs.png';
import uploadIcon from '../../../../assets/ic_upload_file.png';
import OpenAPI from '../../../../endpoint/Openapi';
import useTenantStore, { selectTenantId } from '../../../../stores/TenantStore';
import { RadioButton } from 'primereact/radiobutton';
import { InputText } from 'primereact/inputtext';
import { ColorPicker } from 'primereact/colorpicker';
import { noop } from '../../../../utils/GeneralUtils';
import { type CustomError } from '../../../../models/Error';
interface IFile {
  name: string;
  objectURL: string;
  size: number;
}

function LoginBg() {
  const tenantId = useTenantStore(selectTenantId);
  const [totalSize, setTotalSize] = React.useState(0);
  const [selectedFile, setSelectedFile] = React.useState<File>();
  const [pending, setPending] = React.useState(false);
  const fileUploadRef = React.useRef<FileUpload>(null);
  const [useImage, setUseImage] = React.useState<boolean>(false);
  const [selectedColor, setSelectedColor] = React.useState<string>();
  const [currentFile, setcurrentFile] = React.useState<string>();
  const toast = React.useRef<Toast>(null);
  useEffect(() => {
    const getColors = async () => {
      setPending(true);

      try {
        const res = await OpenAPI.GET('/api/v2/admin/tenant/login-bg', {
          params: { query: { tenantId } },
        });

        setUseImage(res.data?.useImage ?? false);
        setSelectedColor(res.data?.color ?? undefined);
        setcurrentFile(res.data?.imageUrl ?? undefined);
      } catch {}

      setPending(false);
    };

    getColors().catch(noop);
  }, [tenantId]);
  const handleSave = async () => {
    setPending(true);

    try {
      const res = await OpenAPI.POST('/api/v2/admin/tenant/login-bg', {
        params: {
          query: {
            tenantId,
          },
        },
        body: {
          // @ts-expect-error
          File: selectedFile,
          UseImage: useImage,
          Color: selectedColor,
        },
        bodySerializer: (body) => {
          if (!body) return body;

          const formData = new FormData();
          if (body.File) formData.set('file', body.File);
          if (body.UseImage) formData.set('useImage', body.UseImage.toString());
          if (body.Color) formData.set('color', body.Color);
          return formData;
        },
      });
      if (res.error) {
        // @ts-expect-error
        const err = res.error;
        toast.current?.show({
          severity: 'error',
          summary: 'Error occurred',
          detail:
            typeof err === 'string'
              ? err
              : err instanceof Error
                ? err.message
                : (err as CustomError)?.message || 'Unknown error',
          life: 3000,
        });
      } else {
        setSelectedFile(undefined);
        toast.current?.show({
          severity: 'info',
          summary: 'Success',
          detail: 'Icon Uploaded',
        });
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }

    setPending(false);
  };

  const myUploader = (event: FileUploadHandlerEvent) => {
    setSelectedFile(event.files[0]);
    event.options.clear();
  };

  const onTemplateSelect = (e: FileUploadSelectEvent) => {
    setSelectedFile(e.files[0]);
    let _totalSize = totalSize;
    const files = e.files;

    files.forEach((file) => {
      _totalSize += file.size || 0;
    });

    setTotalSize(_totalSize);
  };

  const onTemplateUpload = (e: FileUploadUploadEvent) => {
    let _totalSize = 0;

    e.files.forEach((file) => {
      _totalSize += file.size || 0;
    });

    setTotalSize(_totalSize);
    setSelectedFile(undefined);
    setPending(false);
  };

  const onTemplateRemove = (file: IFile, callback: () => void) => {
    setTotalSize(totalSize - file.size);
    callback();
    setSelectedFile(undefined);
  };

  const onTemplateClear = () => {
    setTotalSize(0);
  };

  const headerTemplate = (options: FileUploadHeaderTemplateOptions) => {
    return (
      <div className="flex flex-row mb-4 align-items-center">
        {options.chooseButton}
      </div>
    );
  };

  const itemTemplate = (file: object, props: ItemTemplateOptions) => {
    return (
      <div className="flex items-center">
        <img
          alt={(file as IFile).name}
          role="presentation"
          src={(file as IFile).objectURL}
          className="w-2/5"
        />
        <div className="flex flex-1 flex-col text-left text-xl ml-3">
          {(file as IFile).name}
          <small>{new Date().toLocaleDateString()}</small>
          <Tag
            value={props.formatSize}
            severity="warning"
            className="px-3 py-2"
          />
        </div>
        <Button
          type="button"
          icon="pi pi-times"
          className="p-button-outlined p-button-rounded p-button-danger ml-3"
          onClick={() => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            onTemplateRemove(file as IFile, props.onRemove);
          }}
        />
      </div>
    );
  };

  const emptyTemplate = () => {
    return (
      <div className="flex flex-col items-center px-5 py-3.5 mt-5 text-sm leading-5 text-zinc-500">
        <img loading="lazy" src={uploadIcon} className="aspect-1 w-[60px]" />
        <div className="mt-11 text-base font-bold leading-6">
          drag and drop.
        </div>
        <div className="mt-2">Maximum file size is 100MB.</div>
        <div className="mt-12">File supported: PNG</div>
      </div>
    );
  };

  return (
    <div>
      {pending ? (
        <Skeleton width="100rem" height="100rem"></Skeleton>
      ) : (
        <div className="gap-4">
          <div className="flex flex-row w-full max-w-screen-lg items-start">
            <div className="flex flex-col mr-4 w-full bg-zinc-700 rounded-3xl">
              <img src={logoScreenshot} />
            </div>

            <div className="flex flex-col justify-center mx-auto w-[40%] text-base leading-6 text-black bg-white">
              <div className="flex flex-col px-6 py-8 w-full bg-white rounded-3xl shadow-[0px_4px_12px_rgba(0,0,0,0.1)]">
                <div className="text-3xl font-medium mb-2">Icon</div>
                <RadioButton
                  inputId="ingredient1"
                  name="LoginBgType"
                  value={true}
                  onChange={(e) => {
                    setUseImage(e.value);
                  }}
                  checked={useImage}
                />

                <label htmlFor="ingredient1" className="ml-2">
                  File
                </label>
                {currentFile && (
                  <p>
                    <div className="statusText">Current file:</div>
                    <img
                      src={currentFile ?? ''}
                      alt="Current file"
                      height="80"
                      width="80"
                    />
                  </p>
                )}
                <FileUpload
                  ref={fileUploadRef}
                  name="bg"
                  url="/api/upload"
                  accept="image/png"
                  maxFileSize={100000000}
                  onUpload={onTemplateUpload}
                  onSelect={onTemplateSelect}
                  onError={onTemplateClear}
                  onClear={onTemplateClear}
                  headerTemplate={headerTemplate}
                  itemTemplate={itemTemplate}
                  emptyTemplate={emptyTemplate}
                  chooseOptions={{
                    label: 'Click to upload',
                    icon: ' ',
                    className: '',
                  }}
                  customUpload={true}
                  uploadHandler={myUploader}
                  contentClassName="bg-white rounded-2xl border border-dashed border-neutral-400 px-0 py-0"
                />
                <div className="mt-5 leading-7 text-zinc-500">
                  If a icon is not provided, the Driverly icon will be used.
                </div>
                <RadioButton
                  inputId="ingredient2"
                  name="LoginBgType"
                  value={false}
                  onChange={(e) => {
                    setUseImage(e.value);
                  }}
                  checked={!useImage}
                />
                <div className="p-inputgroup">
                  <InputText
                    disabled={useImage}
                    value={selectedColor}
                    className="p-inputtext-sm"
                    onChange={(e) => {
                      const newValue = `#${e.target.value}`;
                      setSelectedColor(newValue);
                    }}
                  />
                  <ColorPicker
                    disabled={useImage}
                    className="p-inputgroup-addon w-2"
                    format="hex"
                    value={selectedColor}
                    onChange={(e) => {
                      const newValue = `#${e.value?.toString() ?? ''}`;
                      setSelectedColor(newValue);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-row w-full px-6 py-6 justify-end">
            <Button
              label="Save"
              className={`text-2xl px-14 py-3 bg-[#F1B51C] hover:bg-bluegray-400 text-black border-none`}
              onClick={handleSave}
            />
          </div>
        </div>
      )}

      <Toast ref={toast} className="mt-header-height" />
    </div>
  );
}

export default LoginBg;
